import { camelToDash, dashToCamel, isObjectLike, isNaN, parseAttribute } from "../util/attribute-parser";

class BaseComponent {
    constructor(options = {}) {
        if (!options.element) return;

        this.element = options.element;
        this.$element = $(options.element);
        this._state = {};
        this._mutationObserver = null;
        this._registeredEvents = [];

        const properties = this.properties();
        return new Proxy(this, {
            get: function (target, prop, receiver) {
                if (prop in properties) {
                    return options.element[prop];
                }
                return Reflect.get(...arguments);
            },
            set: function(target, prop, value) {
                if (prop in properties) {
                    options.element[prop] = value;
                } else {
                    target[prop] = value;
                }
                return true;
            }
        });
    }

    connectedCallback() {
        this.defineProperties();

        this.defineObserver();

        this.registerEvents();

        this.triggerHook('connected');
    }

    defineProperties() {
        const ignoreAttributes = ['class', 'style'];

        const properties = {};
        Object.entries(this.properties()).forEach(([name, value]) => {
            properties[name] = this.element[name] || value;
        });
        Array.from(this.element.attributes).filter(attribute => !ignoreAttributes.includes(attribute.name)).forEach(attribute => {
            properties[dashToCamel(attribute.name)] = parseAttribute(attribute.value);
        });

        Object.entries(properties).forEach(([name, value]) => {
            this.defineProperty(name, value);
        });
    }

    defineProperty(name, value) {
        this._state[name] = value;

        Object.defineProperty(this.element, name, {
            get: () => {
                return this._state[name];
            },
            set: (newValue) => {
                const oldValue = this._state[name];
                const newValueString = JSON.stringify(newValue);

                if (JSON.stringify(oldValue) !== newValueString) {
                    this._state[name] = newValue;
                    this.requestUpdate();
                    this.reflectProperty({name, newValue, newValueString});
                    this.notifyPropertyWatcher(name, newValue, oldValue);
                }

                return this;
            },
        });
    }

    reflectProperty(options) {
        const {name, newValue} = options;

        if (!this.element.hasAttribute(name)) return;

        const newValueString = options.newValueString || JSON.stringify(newValue);

        if (newValue === undefined || newValue === null || isNaN(newValue)) {
            // these would be reflected as strings: "undefined" || "null" || "NaN"
            // which is not the desired behaviour. Therefore we reflect them as empty strings
            this.element.setAttribute(camelToDash(name), '');
        } else {
            const attributeValue = isObjectLike(newValue) ? newValueString : newValue;
            this.element.setAttribute(camelToDash(name), attributeValue);
        }
    }

    notifyPropertyWatcher(name, newValue, oldValue) {
        if (name in this.watch()) {
            this.watch()[name](newValue, oldValue);
        }
    }

    // Map of reactive attributes and properties
    // { foo: 'bar' }
    properties() {
        return {};
    }

    // Map of callbacks for attributes and properties to be watched for changes
    // { foo: (foo, oldFoo) => console.log('foo changed', foo, oldFoo) }
    watch() {
        return {};
    }

    defineObserver() {
        // see: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver#Example_usage
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === 'attributes' &&
                    mutation.target === this.element &&
                    this._state.hasOwnProperty(dashToCamel(mutation.attributeName))
                ) {
                    // update property by invoking the setter
                    this.element[dashToCamel(mutation.attributeName)] = parseAttribute(
                        this.element.getAttribute(mutation.attributeName),
                    );
                }
                if (mutation.type === 'attributes' && mutation.target !== this.element) {
                    this.requestUpdate();
                }
                if (mutation.type === 'childList') {
                    this.requestUpdate();
                }
            });
        });

        this._mutationObserver.observe(this.element, {
            attributes: true,
            childList: true,
            subtree: false,
        });
    }

    requestUpdate() {
        this.triggerHook('beforeUpdate');
        this.update();
        this.triggerHook('afterUpdate');
    }

    update() {
        this.registerEvents();
    }

    /**
     * Return a map of events that should be registered on the element.
     * eg. { '.link': {
     *               'click': (e) => {console.log('MyElement.link - clicked', e)},
     *               'mouseover': (e) => {console.log('MyElement.link - mouseover()', e)},
     *           }
     *     }
     */
    events() {
        return {};
    }

    // Registers events on the element
    registerEvents() {
        // remove all previously registered events to prevent duplicate triggers
        this.removeEvents();

        // register events
        const eventDefinitions = this.events();
        Object.keys(eventDefinitions).forEach((elementSelector) => {
            const events = eventDefinitions[elementSelector];
            const selectorWhiteList = {window, document, this: this.element};
            const whiteListElement = selectorWhiteList[elementSelector];
            const eventTargets = whiteListElement
                ? [whiteListElement]
                : this.element.querySelectorAll(elementSelector);

            eventTargets.forEach((eventTarget) => {
                Object.keys(events).forEach((eventName) => {
                    const callback = events[eventName].bind(this);
                    eventTarget.addEventListener(eventName, callback);
                    this._registeredEvents.push({eventTarget, eventName, callback});
                });
            });
        });
    }

    // Removes all registered events on the element
    removeEvents() {
        this._registeredEvents.forEach(({eventTarget, eventName, callback}) => {
            if (eventTarget === window || eventTarget === document || this.element.contains(eventTarget)) {
                eventTarget.removeEventListener(eventName, callback);
            }
        });
        this._registeredEvents = [];
    }

    // Triggers a lifecycle hook based on the name
    triggerHook(name) {
        if (name in this) {
            this[name]();
        }
    }

    // Helper function for dispatching custom events
    dispatch(name, detail = undefined, options) {
        const event = new CustomEvent(name, {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: detail,
            ...options,
        });
        this.element.dispatchEvent(event);
    }
}

export default BaseComponent;
